import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Zap } from "lucide-react";
import { NAV_LINKS, COMPANY } from "@/lib/constants";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white border-b border-zinc-200 py-0"
          : "bg-white border-b border-zinc-100 py-1"
      }`}
    >
      <div className={`max-w-7xl mx-auto px-6 md:px-10 lg:px-12 flex items-center justify-between transition-all duration-500 ${
        scrolled ? "h-14 md:h-16" : "h-16 md:h-20"
      }`}>
        {/* Logo */}
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center group"
        >
          <div className="relative h-12 md:h-14 w-auto">
            <img
              src={COMPANY.logo}
              alt={COMPANY.name || "Prime Volt"}
              className="h-full w-auto object-contain"
            />
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.slice(0, 5).map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={l.testId}
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-semibold tracking-wide uppercase transition-all relative group/link ${
                  isActive
                    ? "text-zinc-950"
                    : "text-zinc-600 hover:text-zinc-950"
                }`
              }
            >
              {l.label}
              <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-[#EAB308] transition-all duration-300 group-hover/link:w-1/2" />
            </NavLink>
          ))}
        </nav>

        {/* Desktop CTA */}
        <div className="hidden lg:flex items-center gap-3">
          <a
            href={`tel:${COMPANY.phone}`}
            data-testid="nav-phone"
            className="text-sm font-semibold text-zinc-700 hover:text-zinc-950"
          >
            {COMPANY.phoneDisplay}
          </a>
          <Link
            to="/contact"
            data-testid="nav-cta"
            className="inline-flex items-center gap-2 bg-[#EAB308] hover:bg-[#CA8A04] active:scale-95 text-navy-950 px-5 py-2.5 text-sm font-bold uppercase tracking-wide transition-all border-2 border-navy-950 shadow-[4px_4px_0px_0px_rgba(11,33,73,1)] hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px]"
          >
            Get a Quote
            <span aria-hidden className="transition-transform group-hover:translate-x-1">→</span>
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(true)}
          className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border border-zinc-300 bg-transparent text-zinc-900 hover:bg-zinc-100 transition-colors"
        >
          <Menu className="h-5 w-5" />
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      <div 
        className={`fixed inset-0 z-[60] bg-navy-950/80 backdrop-blur-sm transition-opacity duration-300 lg:hidden ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none -z-10"
        }`}
        onClick={() => setOpen(false)}
      />

      {/* Mobile Feature Drawer */}
      <div
        data-testid="nav-mobile-drawer"
        className={`fixed top-0 right-0 h-[100dvh] w-[85vw] max-w-sm bg-white shadow-2xl z-[70] transform transition-transform duration-300 ease-in-out lg:hidden flex flex-col ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Drawer Header */}
        <div className="flex items-center justify-between px-6 h-16 md:h-20 border-b border-zinc-100">
          <Link to="/" onClick={() => setOpen(false)} className="h-10">
            <img src={COMPANY.logo} alt={COMPANY.name || "Prime Volt"} className="h-full w-auto object-contain" />
          </Link>
          <button
            onClick={() => setOpen(false)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-zinc-100 text-zinc-900 hover:bg-zinc-200 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Drawer Links */}
        <nav className="flex-1 overflow-y-auto py-8 px-6 flex flex-col gap-2">
          {NAV_LINKS.map((l, i) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              onClick={() => setOpen(false)}
              data-testid={`${l.testId}-mobile`}
              style={{ transitionDelay: `${i * 75}ms` }}
              className={({ isActive }) =>
                `block px-4 py-4 rounded-xl text-lg font-bold tracking-wide transition-all duration-300 transform ${
                  open ? "translate-x-0 opacity-100" : "translate-x-8 opacity-0"
                } ${
                  isActive
                    ? "bg-[#EAB308]/10 text-[#CA8A04]"
                    : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-950"
                }`
              }
            >
              {({ isActive }) => (
                <div className="flex items-center justify-between">
                  <span>{l.label}</span>
                  <span className={`text-xl ${isActive ? "text-[#CA8A04] opacity-100" : "text-zinc-300 opacity-0 -translate-x-2"} transition-all duration-300`}>→</span>
                </div>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Drawer Footer CTA */}
        <div className="p-6 bg-zinc-50 border-t border-zinc-100">
          <div className="flex flex-col gap-4">
            <a
              href={`tel:${COMPANY.phone}`}
              className="flex items-center gap-3 text-sm font-semibold text-zinc-900 hover:text-[#CA8A04] transition-colors"
            >
              <div className="h-10 w-10 rounded-full bg-white shadow-sm border border-zinc-100 flex items-center justify-center text-[#EAB308]">
                📞
              </div>
              {COMPANY.phoneDisplay}
            </a>
            <a
              href={`mailto:${COMPANY.email}`}
              className="flex items-center gap-3 text-sm font-semibold text-zinc-900 hover:text-[#CA8A04] transition-colors break-all"
            >
              <div className="h-10 w-10 rounded-full bg-white shadow-sm border border-zinc-100 flex items-center justify-center text-[#EAB308]">
                ✉
              </div>
              {COMPANY.email}
            </a>
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="mt-2 text-center bg-navy-950 text-white hover:bg-[#EAB308] hover:text-navy-950 py-4 rounded-xl text-sm font-bold uppercase tracking-wider transition-colors shadow-lg"
            >
              Get a Quote
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
};
